<template>
  <div class="bar-chart">
    <BarChart :chart-data="dataCollection" :options="options"></BarChart>
    <div class="legend" v-if="true">
      <table>
        <tr>
          <td colspan="3" class="txt-2">
            {{ labels.monthData }}
            <b>{{ currentLabel.dateMonth || '' }}</b>
            -
            <small>{{ currentLabel.dateFull }}</small>
          </td>
        </tr>
        <tr>
          <td colspan="3"></td>
        </tr>
        <tr>
          <td class="legend-color"><div class="point promotors"></div></td>
          <td class="title">{{ (labels.promoters || '').toUpperCase() }}</td>
          <td class="percent">{{ currentLabel.promoters }}%</td>
        </tr>
        <tr>
          <td><div class="point detractors"></div></td>
          <td class="title">{{ (labels.detractors || '').toUpperCase() }}</td>
          <td class="percent">{{ currentLabel.detractors }}%</td>
        </tr>
        <tr class="line">
          <td></td>
          <td class="title">{{ isLeadership ? labels.lnps : labels.enps }}</td>
          <td class="percent">{{ currentLabel.eNPS }}</td>
        </tr>
        <tr>
          <td colspan="3">{{ labels.othersData }}</td>
        </tr>
        <tr>
          <td><div class="point passives"></div></td>
          <td class="title">{{ (labels.liabilities || '').toUpperCase() }}</td>
          <td class="percent">{{ currentLabel.passives }}%</td>
        </tr>
        <tr>
          <td><div class="point notAnswered"></div></td>
          <td class="title">{{ (labels.noAnswers || '').toUpperCase() }}</td>
          <td class="percent">{{ currentLabel.notAnswered }}%</td>
        </tr>
      </table>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import BarChart from '@/components/Charts/BarChart.js'

const props = {
  enps: {
    required: false,
    type: Array,
    default: function() {
      return []
    },
  },
  isLeadership: {
    required: false,
    type: Boolean,
    default: false,
  },
}

export default {
  props: props,
  components: {
    BarChart,
  },
  data: function() {
    return {
      currentLabel: {},
      inicialClick: true,
    }
  },
  methods: {
    chartClick(event, array) {
      if (!array[0]) return

      const key = array[0]._model.label
      const mounth = this.$moment(key, 'MMM')
        .format('MMMM')
        .replace(/(\w)/, f => f.toLocaleUpperCase())

      this.currentLabel = Object.assign({}, this.enpsMapped[key], {
        date: mounth,
      })
    },
  },
  watch: {
    enpsMapped: function(value) {
      this.chartClick(null, [
        {
          _model: { label: Object.keys(value).reverse()[0] },
        },
      ])
    },
  },
  mounted() {
    const key = Object.keys(this.enpsMapped).reverse()[0]
    this.currentLabel = this.enpsMapped[key]
  },
  computed: {
    ...mapGetters({
      resources: 'resources',
    }),

    labels: function() {
      return Object.assign({}, this.resources.page.reports.engagement.enps)
    },

    enpsMapped: function() {
      const enps = Object.create(this.enps)

      const enps2 = enps.reduce((enps, item) => {
        const itemDate = this.$moment(item.date)
        const dateKey = itemDate
          .format('MMM')
          .replace(/(\w)/, f => f.toLocaleUpperCase())
        enps[dateKey] = {
          date: dateKey,
          dateMonth: itemDate.format('MMMM'),
          dateFull: itemDate.format('DD/MM/YYYY'),
          eNPS: item.score,
          promoters: item.prop_promoters,
          passives: item.prop_passives,
          detractors: item.prop_detractors,
          notAnswered: item.prop_not_answered,
        }
        return enps
      }, [])

      return enps2
    },
    dataCollection: function() {
      const collection = {
        labels: [],
        datasets: [],
      }

      const dataset = {
        promoters: {
          label: this.labels.promoters,
          backgroundColor: 'rgba(35,235,176,0.5)',
          hoverBackgroundColor: 'rgb(35,235,176)',
          borderColor: 'rgba(0,0,0,0)',
          borderWidth: '10',
          data: [],
        },
        passives: {
          label: this.labels.liabilities,
          backgroundColor: 'rgba(237,160,8,0.5)',
          hoverBackgroundColor: 'rgb(237,160,8)',
          borderColor: 'rgba(0,0,0,0)',
          borderWidth: '10',
          data: [],
        },
        detractors: {
          label: this.labels.detractors,
          backgroundColor: 'rgba(235,35,94,0.5)',
          hoverBackgroundColor: 'rgb(235,35,94)',
          borderColor: 'rgba(0,0,0,0)',
          borderWidth: '10',
          data: [],
        },
        notAnswered: {
          label: this.labels.notApply,
          borderWidth: '10',
          backgroundColor: 'rgba(246,247,253,0.8)',
          hoverBackgroundColor: 'rgb(246,247,253)',
          borderColor: 'rgba(0,0,0,0)',
          data: [],
        },
      }

      collection.labels = Object.keys(this.enpsMapped)
      collection.datasets = Object.values(
        Object.values(this.enpsMapped).reduce((previous, current) => {
          previous.promoters.data.push(current.promoters)
          previous.passives.data.push(current.passives)
          previous.detractors.data.push(current.detractors)
          previous.notAnswered.data.push(current.notAnswered)
          return previous
        }, dataset)
      )

      return collection
    },
    options: function() {
      return {
        cornerRadius: 40,
        borderDashOffset: 3,
        responsive: true,
        maintainAspectRatio: false,
        tooltips: {
          enabled: true,
          callbacks: {
            label: function(tooltipItems) {
              return tooltipItems.yLabel + '%'
            },
          },
        },
        onClick: this.chartClick,
        legend: {
          display: false,
        },
        scales: {
          xAxes: [
            {
              stacked: true,
              gridLines: {
                display: false,
                color: 'rgba(0, 0, 0, 0)',
              },
            },
          ],
          yAxes: [
            {
              stacked: true,
              ticks: {
                max: 100,
              },
              gridLines: {
                display: false,
                color: 'rgba(0, 0, 0, 0)',
              },
            },
          ],
        },
      }
    },
  },
}
</script>

<style lang="scss">
@import '~@/styles/custom/colors';

#bar-chart {
  position: relative;
  margin: auto;
  height: 250px !important;
  max-width: 100%;
  @media (max-width: 1024px) {
    height: 190px !important;
  }
}

.legend {
  width: 100%;
  background: map-get($theme-colors, 'light');
  display: flex;
  justify-content: space-evenly;
  color: map-get($theme-colors, 'gray03');
  font-size: 12px;
  font-weight: 500;
  align-items: center;
  padding: 1.5rem;

  table {
    min-width: 215px;
    width: 100%;

    @media (max-width: 767px) {
      margin: 0.5rem 0;
    }

    th {
      text-align: center;
    }

    tr {
      td {
        padding: 7px 10px;
      }

      .txt-2 {
        font-size: 1.1rem;
        font-weight: normal;
      }

      .legend-color {
        width: 10px;
      }

      .point {
        width: 10px;
        height: 10px;
        border-radius: 5px;

        &.promotors {
          background: map-get($theme-colors, 'lima');
        }

        &.detractors {
          background: map-get($theme-colors, 'secondary');
        }

        &.passives {
          background: map-get($theme-colors, 'orange');
        }

        &.notAnswered {
          background: map-get($theme-colors, 'white');
          border: 1px solid map-get($theme-colors, 'gray07');
        }
      }
      &.line {
        border-top: 1px solid map-get($theme-colors, 'gray07');
        color: map-get($theme-colors, 'septenary');
      }

      .percent {
        text-align: right;
      }
    }
  }
}
</style>
